import React, { useState, useEffect, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import './App.css';
import { Toaster } from './components/ui/sonner';
import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import SepaPage from './pages/SepaPage';
import LogboekPage from './pages/LogboekPage';
import InstellingenPage from './pages/InstellingenPage';
import MaandafsluitingPage from './pages/MaandafsluitingPage';
import PtwImportPage from './pages/PtwImportPage';
import IncassosPage from './pages/IncassosPage';
import BetalingenPage from './pages/BetalingenPage';
import SalarisPage from './pages/SalarisPage';
import StornoPage from './pages/StornoPage';
import Tw1MemoPage from './pages/Tw1MemoPage';
import TruckshopMemoPage from './pages/TruckshopMemoPage';
import TeamchatPage from './pages/TeamchatPage';
import PleoDoorbelastingPage from './pages/PleoDoorbelastingPage';
import Sidebar from './components/tw1/Sidebar';
import GlobalSearch from './components/tw1/GlobalSearch';
import Screensaver from './components/tw1/Screensaver';
import { getUser, getToken, setAuth, hasPageAccess, clearAuth } from './lib/auth';
import api from './lib/api';
import GuidedTour from './components/tw1/GuidedTour';

const pageTransition = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: { duration: 0.25, ease: [0.25, 0.1, 0.25, 1] },
};

const TOUR_STEPS = [
  { target: '[data-testid="sidebar-nav-dashboard"]', content: 'Dit is het Dashboard — hier zie je je taken, statistieken en een overzicht van recente exports.' },
  { target: '.tw1-sidebar', content: 'De sidebar — navigeer snel naar alle modules van het portaal.' },
  { target: '[data-testid="sidebar-nav-maandafsluiting"]', content: 'Maandafsluiting — bereken hier de omzet, fees en doorbelasting per locatie.' },
  { target: '[data-testid="sidebar-nav-sepa"]', content: 'SEPA Betaalbatch — genereer XML bestanden voor bulk betalingen.' },
  { target: '[data-testid="sidebar-nav-logboek"]', content: 'Logboek — bekijk en download alle eerder gegenereerde exports.' },
  { target: '[data-testid="sidebar-nav-instellingen"]', content: 'Instellingen — beheer gebruikers, rechten, en account instellingen.' },
];

function App() {
  const [user, setUser] = useState(getUser());
  const [activePage, setActivePage] = useState('dashboard');
  const [screensaverTimeout, setScreensaverTimeout] = useState(5);
  const [runTour, setRunTour] = useState(false);

  // Fetch latest user data & app settings on mount
  useEffect(() => {
    const token = getToken();
    const storedUser = getUser();
    if (token && storedUser) {
      setUser(storedUser);
      // Refresh user data (permissions may have changed)
      api.get('/api/auth/me').then(res => {
        const updated = { ...storedUser, ...res.data };
        setAuth(token, updated);
        setUser(updated);
      }).catch(() => {});
      // Load screensaver timeout
      api.get('/api/settings/app').then(res => {
        setScreensaverTimeout(res.data.screensaver_timeout || 5);
      }).catch(() => {});
    }
  }, []);

  // Session heartbeat every 60 seconds
  useEffect(() => {
    if (!user || !getToken()) return;
    const heartbeat = () => api.post('/api/sessions/heartbeat').catch(() => {});
    heartbeat();
    const interval = setInterval(heartbeat, 60000);
    return () => clearInterval(interval);
  }, [user]);

  // Clear session on logout/unload
  useEffect(() => {
    const handleUnload = () => {
      if (getToken()) {
        navigator.sendBeacon(
          `${process.env.REACT_APP_BACKEND_URL}/api/sessions`,
          new Blob([JSON.stringify({})], { type: 'application/json' })
        );
      }
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
    setActivePage('dashboard');
    // Load screensaver timeout after login
    api.get('/api/settings/app').then(res => {
      setScreensaverTimeout(res.data.screensaver_timeout || 5);
    }).catch(() => {});
    // Check if tour should run
    api.get('/api/auth/me').then(res => {
      if (!res.data.tour_completed) {
        setTimeout(() => setRunTour(true), 1500);
      }
    }).catch(() => {});
  };

  const handleTourCallback = () => {
    setRunTour(false);
    api.post('/api/auth/tour-complete').catch(() => {});
  };

  const handleNavigate = useCallback((page) => {
    // Check permissions before navigating
    if (hasPageAccess(page)) {
      setActivePage(page);
      // Log page visit
      api.post('/api/activity', { actie: 'paginabezoek', details: page, module: page }).catch(() => {});
    } else {
      setActivePage('dashboard');
    }
  }, []);

  // Not logged in
  if (!user || !getToken()) {
    // Check if on reset-password route
    const params = new URLSearchParams(window.location.search);
    if (window.location.pathname === '/reset-password' && params.get('token')) {
      return (
        <>
          <ResetPasswordPage />
          <Toaster position="top-right" />
        </>
      );
    }
    return (
      <>
        <LoginPage onLogin={handleLogin} />
        <Toaster position="top-right" />
      </>
    );
  }

  // Render active page (with permission check)
  const renderPage = () => {
    const page = hasPageAccess(activePage) ? activePage : 'dashboard';
    switch (page) {
      case 'dashboard':
        return <DashboardPage onNavigate={handleNavigate} />;
      case 'tw1-memo':
        return <Tw1MemoPage />;
      case 'truckshop-memo':
        return <TruckshopMemoPage />;
      case 'maandafsluiting':
        return <MaandafsluitingPage />;
      case 'ptw-import':
        return <PtwImportPage />;
      case 'incassos':
        return <IncassosPage />;
      case 'betalingen':
        return <BetalingenPage />;
      case 'salaris':
        return <SalarisPage />;
      case 'sepa':
        return <SepaPage />;
      case 'storno':
        return <StornoPage />;
      case 'pleo':
        return <PleoDoorbelastingPage />;
      case 'teamchat':
        return <TeamchatPage />;
      case 'logboek':
        return <LogboekPage />;
      case 'instellingen':
        return <InstellingenPage />;
      default:
        return <DashboardPage onNavigate={handleNavigate} />;
    }
  };

  return (
    <div className="flex min-h-screen bg-tw1-bg">
      <Sidebar activePage={activePage} onNavigate={handleNavigate} />
      <main className="flex-1 min-h-screen overflow-y-auto">
        {/* Top bar with search */}
        <div className="hidden lg:flex items-center justify-end px-6 py-3 border-b border-gray-100 bg-white/80 backdrop-blur-sm sticky top-0 z-30">
          <GlobalSearch onNavigate={handleNavigate} />
        </div>
        <div className="p-4 lg:p-6 max-w-[1280px] pt-16 lg:pt-4">
          <AnimatePresence mode="wait">
            <motion.div
              key={activePage}
              initial={pageTransition.initial}
              animate={pageTransition.animate}
              exit={pageTransition.exit}
              transition={pageTransition.transition}
            >
              {renderPage()}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>
      <GuidedTour steps={TOUR_STEPS} run={runTour} onComplete={handleTourCallback} />
      <Screensaver timeoutMinutes={screensaverTimeout} />
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
